import { useEffect, useMemo, useRef } from 'react';

import { useStrings } from '../i18n/context';
import { transcriptFor, type SubtitleLine } from '../streams';

interface Props {
  readonly open: boolean;
  readonly lines: readonly SubtitleLine[];
  /** The reader's language: the text is shown in it (streams.ts: transcriptFor). */
  readonly language: string;
  readonly names: ReadonlyMap<string, string>;
  readonly onClose: () => void;
}

function timeOf(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Everything said in the meeting, sentence by sentence, in the reader's language: the
 * speaker's own words when they spoke it, the interpreter's translation otherwise. Every
 * participant has it. Stays mounted while closed, so it is complete when opened. Text is
 * rendered as text, never as HTML.
 */
export function TranscriptPanel({ open, lines, language, names, onClose }: Props) {
  const strings = useStrings();
  const entries = useMemo(() => transcriptFor(lines, language), [lines, language]);
  const list = useRef<HTMLOListElement>(null);

  // Follow the newest sentence while the reader is at the bottom.
  useEffect(() => {
    const element = list.current;
    if (!element || !open || entries.length === 0) return;
    const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
    if (nearBottom) element.scrollTop = element.scrollHeight;
  }, [entries, open]);

  return (
    <aside id="vm-transcript" className="notes transcript" aria-labelledby="vm-transcript-heading" hidden={!open}>
      <div className="notes__header">
        <h2 id="vm-transcript-heading">{strings.transcriptHeading}</h2>
        <button type="button" className="button" onClick={onClose}>
          {strings.transcriptClose}
        </button>
      </div>
      {entries.length === 0 ? (
        <p className="notes__empty">{strings.transcriptEmpty}</p>
      ) : (
        <ol ref={list} className="panel__list">
          {entries.map((entry) => (
            <li key={entry.key} className="panel__entry">
              <span className="panel__meta">
                <time>{timeOf(entry.at)}</time> {names.get(entry.speaker) || strings.someone}
              </span>
              {entry.unclear ? (
                <span className="panel__note">{strings.subtitleUnclear}</span>
              ) : entry.text === null ? (
                <span className="panel__note">{strings.subtitleTranslating}</span>
              ) : (
                <span lang={language}>{entry.text}</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}
